import { Dispatch, MouseEventHandler, ReactElement, SetStateAction } from 'react';
import { space } from '@op/opds.tokens';
import { Message, useMessageGetter } from '@messageformat/react';
import { Col, Form, Row, Tile } from '@op/opds.opux';
import ResetButton from './ResetButton';
import StepperFormRow from './FormRow';

interface TabProps {
  etfInitialInvestment: number;
  etfMonthlyInvestment: number;
  etfInvestmentPeriod: number;
  etfAnnualReturn: number;
  etfTradingCommission: number;
  etfMinimumFee: number;
  etfPriceCeiling: number;
  etfCurrencyChangeExpense: number;
  etfRunningCosts: number;
  etfOperatingCost: number;
  setEtfInitialInvestment: Dispatch<SetStateAction<number>>;
  setEtfMonthlyInvestment: Dispatch<SetStateAction<number>>;
  setEtfInvestmentPeriod: Dispatch<SetStateAction<number>>;
  setEtfAnnualReturn: Dispatch<SetStateAction<number>>;
  setEtfTradingCommission: Dispatch<SetStateAction<number>>;
  setEtfMinimumFee: Dispatch<SetStateAction<number>>;
  setEtfPriceCeiling: Dispatch<SetStateAction<number>>;
  setEtfCurrencyChangeExpense: Dispatch<SetStateAction<number>>;
  setEtfRunningCosts: Dispatch<SetStateAction<number>>;
  setEtfOperatingCost: Dispatch<SetStateAction<number>>;
  resetValues: MouseEventHandler<HTMLButtonElement>;
  resetPressed: Boolean;
}

const EtfsTab = ({
  etfInitialInvestment,
  etfMonthlyInvestment,
  etfInvestmentPeriod,
  etfAnnualReturn,
  etfTradingCommission,
  etfMinimumFee,
  etfPriceCeiling,
  etfCurrencyChangeExpense,
  etfRunningCosts,
  etfOperatingCost,
  setEtfInitialInvestment,
  setEtfMonthlyInvestment,
  setEtfInvestmentPeriod,
  setEtfAnnualReturn,
  setEtfTradingCommission,
  setEtfMinimumFee,
  setEtfPriceCeiling,
  setEtfCurrencyChangeExpense,
  setEtfRunningCosts,
  setEtfOperatingCost,
  resetValues,
  resetPressed,
}: TabProps): ReactElement => {
  const getText = useMessageGetter('course6.task1');
  return (
    <div>
      <p className="investoracademy-stepper-tab-body">
        <Message id="course6.task1.tab3.body" />
      </p>
      <Tile style={{ marginTop: space.default, width: '100%' }}>
        <Row>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title1" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper1_label')}
                value={etfInitialInvestment}
                min={0}
                max={50000}
                step={500}
                unit="€"
                onChange={setEtfInitialInvestment}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper2_label')}
                value={etfMonthlyInvestment}
                min={50}
                max={1000}
                step={50}
                unit="€"
                onChange={setEtfMonthlyInvestment}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper3_label')}
                value={etfInvestmentPeriod}
                min={1}
                max={30}
                step={1}
                unit={getText('abbr.year')}
                onChange={setEtfInvestmentPeriod}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper4_label')}
                value={etfAnnualReturn}
                min={-100}
                max={100}
                step={1}
                unit="%"
                onChange={setEtfAnnualReturn}
              />
            </Form>
          </Col>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title3" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper5_label')}
                value={etfTradingCommission}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setEtfTradingCommission}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper6_label')}
                value={etfMinimumFee}
                min={0}
                max={50}
                step={1}
                unit="€"
                onChange={setEtfMinimumFee}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper7_label')}
                value={etfPriceCeiling}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setEtfPriceCeiling}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab3.stepper8_label')}
                value={etfCurrencyChangeExpense}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setEtfCurrencyChangeExpense}
              />
            </Form>
          </Col>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={12}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title4" />
            </h4>
            <Row>
              <Col style={{ display: 'block' }} xs={12} md={6}>
                <Form spacing="small" className="investoracademy-stepper-form">
                  <StepperFormRow
                    aria-hidden="true"
                    label={getText('tab3.stepper9_label')}
                    value={etfRunningCosts}
                    min={0}
                    max={10}
                    step={0.1}
                    unit="%"
                    onChange={setEtfRunningCosts}
                  />
                </Form>
              </Col>
              <Col style={{ display: 'block' }} xs={12} md={6}>
                <Form spacing="small" className="investoracademy-stepper-form">
                  <StepperFormRow
                    aria-hidden="true"
                    label={getText('tab3.stepper10_label')}
                    value={etfOperatingCost}
                    min={0}
                    max={10}
                    step={0.1}
                    unit="%"
                    onChange={setEtfOperatingCost}
                  />
                </Form>
              </Col>
            </Row>
          </Col>
        </Row>
        <ResetButton onClick={resetValues} />
        <p
          className="investoracademy-total-text investoracademy-textHighlight-small"
          style={{ textAlign: 'center', paddingTop: space.small }}
          aria-live="polite"
        >
          {resetPressed && <Message id="course6.task1.reset_text" />}
        </p>
      </Tile>
    </div>
  );
};

export default EtfsTab;
